import { type UseMutationResult } from "@tanstack/react-query";
import { Utils } from "common/utils";
import { LabelledRow } from "components";
import React from "react";
import { Button, Input, Message, TextArea } from "semantic-ui-react";
import { type SetParticipantFromContactParams } from "../Participant/hooks";
import ChooseTemplateButton from "components/MessageTemplate/ChooseTemplateButton";
import { getInvitationTranslation } from "components/MessageTemplate/hooks";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { isNullOrEmptyString } from "utilities";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { getCultureFromLcid } from "utilities/enumutils";

interface Props {
  contactId: number
  contactName: string
  initialEmail: string
  initialCompany: server.dto.CompanySmall
  initialCulture: server.dto.Culture

  initialInvitationSubject: string
  initialInvitationBody: string

  isViewer?: boolean
  onPreviousStepClicked?: () => void

  saveMutation: UseMutationResult<string, unknown, SetParticipantFromContactParams, unknown>
  messageTemplateType: server.dto.MessageTemplateTypes
}

const EditParticipantAsContact = (props: Props) => {
  const [company, setCompany] = React.useState<server.dto.CompanySmall>(props.initialCompany);
  const [email, setEmail] = React.useState(props.initialEmail ?? "");
  const [culture, setCulture] = React.useState<server.dto.Culture>(props.initialCulture);

  const [invitationSubject, setInvitationSubject] = React.useState<string>(props.initialInvitationSubject ?? "");
  const [invitationBody, setInvitationBody] = React.useState(props.initialInvitationBody ?? "");

  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>(undefined);

  const loadDefaultInvitationMessageTemplate = async (culture: server.dto.Culture) => {
    if (culture != null) {
      const defaultTemplate = await window.CoreApi.MessageTemplate.PreferredInvitationTemplateTranslation({ type: props.messageTemplateType, language: culture.language });

      setInvitationSubject(defaultTemplate.data.data.subject);
      setInvitationBody(defaultTemplate.data.data.body);
    }
  };

  const hasLoadedInitialTemplate = React.useRef(false);

  React.useEffect(() => {
    // Only load the default template when creating a new participant
    if (hasLoadedInitialTemplate.current) return;

    const isEditingParticipant = props.onPreviousStepClicked == null;
    if (isEditingParticipant) return;

    hasLoadedInitialTemplate.current = true;

    if (culture == null) return;

    window.CoreApi.MessageTemplate.PreferredInvitationTemplateTranslation({ type: props.messageTemplateType, language: culture.language })
      .then(async (res) => {
        setInvitationSubject(res.data.data.subject);
        setInvitationBody(res.data.data.body);
      });

  }, [props.isViewer, props.onPreviousStepClicked]);

  const emailHasError = () => {
    if (email.trim() === "") return true;
    return !Utils.isValidEmailAddress(email);
  };

  const onCultureChanged = (culture: server.dto.Culture) => {
    setCulture(culture);

    if (isNullOrEmptyString(invitationBody)) {
      loadDefaultInvitationMessageTemplate(culture);
    }
  };

  const canSubmit = !emailHasError() && (props.isViewer || company != null) && culture != null;

  const onSubmit = async (e) => {
    e?.preventDefault();
    setHasSubmitted(true);

    if (!canSubmit) return;

    try {
      const res = await props.saveMutation.mutateAsync({
        contactId: props.contactId,
        companyId: company?.companyId,
        email,
        culture: culture.lcid,
        invitationSubject,
        invitationBody
      });

      if (typeof res === "string") {
        setErrorMessage(res);
      }
    } catch {
      setErrorMessage(RESX.GeneralLabels.SomethingUnexpectedHappened);
    }
  };

  return <>
    <div className="flex-column grow1 gap scrollbox-auto">
      <table className="s1_table bordergrid gray">
        <tbody>
          <LabelledRow label={RESX.GeneralLabels.Name} bigPadding>
            <h3>{props.contactName}</h3>
          </LabelledRow>

          <LabelledRow label={RESX.GeneralLabels.Email}>
            <Input
              error={hasSubmitted && emailHasError()}
              maxLength={serverReference.constants.mailAddress.maxEmailLength}
              fluid
              value={email}
              onChange={(_, { value }) => { setEmail(value); }}
            />
          </LabelledRow>

          <LabelledRow label={RESX.Language.resxLanguage}>
            <CulturesTemplateDropdown
              value={culture?.lcid}
              onChange={async (lcid) => {
                const selectedCulture = getCultureFromLcid(lcid);
                onCultureChanged(selectedCulture);

                const preferredTemplate = await window.CoreApi.MessageTemplate.PreferredInvitationTemplateTranslation({ type: props.messageTemplateType, language: selectedCulture.language });
                setInvitationSubject(preferredTemplate.data.data.subject ?? "");
                setInvitationBody(preferredTemplate.data.data.body ?? "");
              }}
            />
          </LabelledRow>

          {!props.isViewer && <tr>
            <td className="label">{RESX.ESign.Representing}</td>
            <td>
              <CompanyModalPicker
                value={company}
                onChange={setCompany}
                modalTitle={RESX.Company.resxCompany}
                addressType="administrative"
                legacyHasError={hasSubmitted && !company?.companyId}
                canCreateNew={false}
                validatorKey="participantCompany"
                required
                hideEditButton
              />
            </td>
          </tr>}
        </tbody>
      </table>

      <div className="flex">
        <div className="grow1"></div>
        <div>
          <ChooseTemplateButton
            type={props.messageTemplateType}
            buttonLabel={RESX.InvitationTemplate.resxInvitationTemplate}
            onTemplateSelected={async (template, language) => {
              const invitationTranslation = await getInvitationTranslation(template.messageTemplateId, language);
              setInvitationSubject(invitationTranslation.subject);
              setInvitationBody(invitationTranslation.body);
            }}
          />
        </div>
      </div>

      <div className="flex-column gap">
        <div>
          <OptionalTextInput
            value={invitationSubject}
            onChange={setInvitationSubject}
            placeholder={RESX.Message.Subject}
            maxLength={serverReference.constants.messageTemplate.maxTranslationSubjectLength}
          />
        </div>
        <div>
          <TextArea
            style={{ minHeight: 200 }}
            placeholder={RESX.Invitation.WriteAMessageThatWillBeDisplayed}
            value={invitationBody}
            rows={12}
            maxLength={serverReference.constants.messageTemplate.maxTranslationBodyLength}
            onChange={(_, { value }) => { setInvitationBody(value.toString()); }}
          />
        </div>
      </div>
    </div>

    {errorMessage && <Message className="margin-top" style={{ minHeight: "3em" }} color="red">
      {errorMessage}
    </Message>}

    <div className="flex">
      <div className="grow1">
        {props.onPreviousStepClicked && <Button color="blue" onClick={props.onPreviousStepClicked} type="button">
          {RESX.GeneralLabels.Previous}
        </Button>}
      </div>
      <div>
        <Button disabled={props.saveMutation.isLoading} loading={props.saveMutation.isLoading} primary onClick={onSubmit} type="button">
          {RESX.Buttons.btnSave}
        </Button>
      </div>
    </div>
  </>;
};
export default EditParticipantAsContact;